import React, { useEffect, useMemo, useRef, useState } from 'react';
import JoditEditor, { Jodit } from 'jodit-react';
import { generateComponentDisplayName } from '../../utils';
import { RichTextEditorGlobalStyle, StyledRichTextEditor } from './RichTextEditor.styles';
import { pxToRem } from '@withjoy/joykit/theme';
import { HeadingButton, RichTextEditorProps } from './RichTextEditor.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useIsMounted } from '@withjoy/joykit/hooks';
import { CustomLinkPlugin } from './plugins/CustomLink/CustomLink';
import { DATA_JOY_DATA } from './plugins/CustomLink/constants';
import { CustomCssDetectionPlugin } from './plugins/CustomCssDetection';
import { withWindow } from '@shared/utils/withWindow';

const EMPTY_NODE = '<p><br></p>';

const RichTextEditor: React.FC<RichTextEditorProps> = React.memo(props => {
  const { content, theme, overrides, handleChange, ...configInitial } = props;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const isMounted = useIsMounted();

  const handleFocus = useEventCallback(() => setIsFocused(true));

  const handleBlur = useEventCallback((value: string) => {
    handleChange(value === EMPTY_NODE ? '' : value);
    if (!document.hasFocus() || !containerRef.current?.contains(document.activeElement)) {
      setIsFocused(false);
    }
  });

  const handleClickOutside = useEventCallback(event => {
    if (containerRef.current && !containerRef.current.contains(event.target)) {
      setIsFocused(false);
    }
  });

  useEffect(() => {
    withWindow(globalWindow => {
      globalWindow.addEventListener('mousedown', handleClickOutside);
    });
    return () => {
      withWindow(globalWindow => {
        globalWindow.removeEventListener('mousedown', handleClickOutside);
      });
    };
  }, [handleClickOutside]);

  // Prevent re-render which cuase issue in config
  const richTextEditor = useMemo(
    () => (
      <JoditEditor
        value={content && content !== '' ? content : EMPTY_NODE}
        config={{ ...configInitial, events: { focus: handleFocus } }}
        onBlur={handleBlur}
      />
    ),
    // All the values are static, so no need to re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <StyledRichTextEditor
      ref={containerRef}
      className="jodit-editor"
    >
      <RichTextEditorGlobalStyle
        isFocused={isFocused}
        theme={theme}
        overrides={overrides}
        minHeight={configInitial.minHeight}
      />
      {isMounted && richTextEditor}
    </StyledRichTextEditor>
  );
});

// Find all buttons - https://xdsoft.net/jodit/play.html?defaultMode=1&buttons=bold%2Citalic%2Cunderline%2Cstrikethrough%2Ceraser%2Cul%2Col%2Cfont%2Cfontsize%2Cparagraph%2ClineHeight%2Csuperscript%2Csubscript%2CclassSpan%2Cfile%2Cimage%2Cvideo%2Cspellcheck%2CspeechRecognize&currentTab=Buttons
const BUTTONS = Jodit.atom(['paragraph', 'bold', 'italic', 'underline', 'link', 'align']);

Jodit.plugins.add('customLinkPlugin', CustomLinkPlugin);
Jodit.plugins.add('customCssDetectionPlugin', CustomCssDetectionPlugin);

RichTextEditor.defaultProps = {
  readonly: false,
  disabled: false,
  tabIndex: 1,
  enter: 'p',
  enterBlock: 'p',
  height: 'auto',
  minHeight: pxToRem(387),
  statusbar: false,
  toolbarInlineForSelection: true,
  toolbarAdaptive: false,
  removeEmptyElements: true,
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  controls: {
    paragraph: {
      update: (_: Jodit, button: HeadingButton) => {
        const control = button.control;
        const currentValue = button.state.value;
        const list = control.list;
        button.state.text = list[currentValue.toString()].toString();
      },
    },
  },
  link: {
    noFollowCheckbox: false,
    openInNewTabCheckbox: false,
    modeClassName: false,
  },
  cleanHTML: {
    allowTags: {
      h1: true,
      h2: true,
      h3: true,
      h4: true,
      h5: true,
      h6: true,
      p: true,
      div: true,
      br: true,
      span: true,
      pre: true,
      blockquote: true,
      strong: true,
      em: true,
      u: true,
      a: {
        href: true,
        [DATA_JOY_DATA]: true,
      },
      allowAttributes: {
        a: [DATA_JOY_DATA],
      },
    },
  },
  popup: {
    selection: BUTTONS,
  },
  buttons: BUTTONS,
  disablePlugins: ['customLinkPlugin'],
};

RichTextEditor.displayName = generateComponentDisplayName('RichTextEditor');

export { RichTextEditor };
